import { JSX, PropsWithChildren, useContext } from "react";
import { useI18n } from "../../i18n";
import classNames from "classnames";
import styles from "./FormFieldWithFieldset.module.css";
import { ErrorMessage } from "../ErrorMessage";
import { AppContext, errorId, hasWarningForField, warningId } from "../../state";

export type FormFieldWithFieldsetProps = {
  id: string;
  tracking_id?: string;
  label?: string;
  errorMessage?: string;
  isOptionalField?: boolean;
  className?: string;
} & PropsWithChildren;

const FormFieldWithFieldset = (props: FormFieldWithFieldsetProps): JSX.Element => {
  const {
    children,
    id,
    tracking_id = undefined,
    label = null,
    errorMessage = null,
    className = "",
    isOptionalField = false,
  } = props;

  const { t } = useI18n();
  const state = useContext(AppContext);
  const fieldClassNames = classNames(styles.formField, className);
  const hasErrorMessage = !!errorMessage;
  const hasWarningMessage = hasWarningForField(state, id);
  const describedBy = [hasErrorMessage && errorId(id), hasWarningMessage && warningId(id)].filter(Boolean).join(" ");
  return (
    <fieldset
      className={fieldClassNames}
      id={tracking_id}
      aria-describedby={describedBy || undefined}
      aria-invalid={hasErrorMessage || hasWarningMessage}
    >
      {label !== null && (
        <legend id={`${id}_label`} className={styles.label} data-testid="formfieldLabel">
          {t(label)}
          {isOptionalField && <span className={styles.optional}> {t("optional")}</span>}
        </legend>
      )}
      <div id={id}>{children}</div>
      {errorMessage !== null && <ErrorMessage message={errorMessage} messageReferenceId={errorId(id)} />}
    </fieldset>
  );
};

export { FormFieldWithFieldset };
